import {Typography, Box} from '@mui/material'
import React from 'react'

import {SelectedSplitLoadMaterials} from '../../containers/SplitLoad/types'
import {TFunction} from 'i18next'

/**
 * Gets the display text for split load materials count
 * @param splitLoadMaterials - The selected split load materials for a specific order
 * @param t - Translation function
 * @returns Display string for material count
 */
export const getSplitLoadMaterialsCount = (
  splitLoadMaterials?: SelectedSplitLoadMaterials,
  t?: TFunction
): string => {
  if (!splitLoadMaterials || !splitLoadMaterials.materials?.length) {
    return '-'
  }
  const count = splitLoadMaterials.materials.length
  const materialType = splitLoadMaterials.materialType?.name

  if (t) {
    return t('orderIntake.splitLoad.materialCount', {
      count,
      materialType: t(`orderIntake.splitLoad.materialTypes.${materialType}`)
    })
  }

  return `${count} ${materialType} materials`
} /**
 * Gets the plant name for split load materials
 * @param splitLoadMaterials - The selected split load materials for a specific order
 * @returns Plant name or '-' if not available
 */
export const getSplitLoadPlantName = (splitLoadMaterials?: SelectedSplitLoadMaterials): string => {
  if (!splitLoadMaterials || !splitLoadMaterials.plant) {
    return '-'
  }
  return splitLoadMaterials.plant.plantName
}

/**
 * Gets the total quantity for all split load materials combined
 * @param splitLoadMaterials - The selected split load materials for a specific order
 * @param numberOfDecimals - Number of decimal places to display
 * @returns Total quantity or '-' if not available
 */
export const getSplitLoadTotalQuantity = (
  splitLoadMaterials?: SelectedSplitLoadMaterials,
  numberOfDecimals: number = 1
): string => {
  if (!splitLoadMaterials || !splitLoadMaterials.materials?.length) {
    return '-'
  }

  const totalQuantity = splitLoadMaterials.materials.reduce((sum, material) => {
    return sum + (material.capacity?.quantity || 0)
  }, 0)

  return totalQuantity.toFixed(numberOfDecimals)
}

/**
 * Renders the split load material display for the table cell
 * @param splitLoadMaterials - The selected split load materials for a specific order
 * @param t - Translation function
 * @returns JSX element for material display
 */
export const renderSplitLoadMaterialDisplay = (
  splitLoadMaterials?: SelectedSplitLoadMaterials,
  t?: TFunction
): React.ReactElement => {
  const materialsCount = getSplitLoadMaterialsCount(splitLoadMaterials, t)
  const plantName = getSplitLoadPlantName(splitLoadMaterials)

  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="body2">{materialsCount}</Typography>
      <Typography component="div" variant="caption" color="secondary">
        {t
          ? `${t('orderIntake.plant')} ${plantName}`.toUpperCase()
          : `PLANT ${plantName}`.toUpperCase()}
      </Typography>
    </Box>
  )
}

/**
 * Renders the split load quantity display for the table cell
 * @param splitLoadMaterials - The selected split load materials for a specific order
 * @param numberOfDecimals - Number of decimal places to display
 * @returns JSX element for quantity display
 */
export const renderSplitLoadQuantityDisplay = (
  splitLoadMaterials?: SelectedSplitLoadMaterials,
  numberOfDecimals: number = 1
): React.ReactElement => {
  const totalQuantity = getSplitLoadTotalQuantity(splitLoadMaterials, numberOfDecimals)

  return <Typography variant="body2">{totalQuantity}</Typography>
}
